/**
 * ViewportCamera —— 持有真实 THREE.Camera 实例，
 * 负责将 CameraRig 语义状态同步到投影参数。
 */
import * as THREE from 'three'
import {
  EPSILON,
  EPSILON_ULTRA,
  ORTHOGRAPHIC_EQUIVALENT_FOV_USER_DATA_KEY,
} from '@/shared/constants'
import type { CameraState, Disposable, ViewportRect } from '@/shared/types/core'
import { CameraMode } from '@/shared/types/core'

/** 持有真实 THREE.Camera 实例，负责将 CameraRig 语义状态同步到投影参数 */
export class ViewportCamera implements Disposable {
  /** 透视相机实例 */
  readonly perspectiveCamera: THREE.PerspectiveCamera
  /** 正交相机实例 */
  readonly orthographicCamera: THREE.OrthographicCamera

  /** 当前激活的投影模式 */
  private _mode: CameraMode = CameraMode.Perspective

  constructor() {
    this.perspectiveCamera = new THREE.PerspectiveCamera()
    this.orthographicCamera = new THREE.OrthographicCamera()
  }

  /**
   * 根据当前 mode 返回对应的 THREE.Camera。
   *
   * @returns 透视或正交相机
   */
  getActiveCamera(): THREE.Camera {
    return this._mode === CameraMode.Perspective ? this.perspectiveCamera : this.orthographicCamera
  }

  /**
   * 获取透视相机实例。
   *
   * @returns 透视相机
   */
  getPerspectiveCamera(): THREE.PerspectiveCamera {
    return this.perspectiveCamera
  }

  /**
   * 获取正交相机实例。
   *
   * @returns 正交相机
   */
  getOrthographicCamera(): THREE.OrthographicCamera {
    return this.orthographicCamera
  }

  /**
   * 将 CameraRig 的语义状态同步到底层 THREE.Camera 的投影矩阵。
   *
   * @param state - 相机状态
   * @param near - 近裁剪距离
   * @param far - 远裁剪距离
   * @param viewport - 视口矩形，宽高 ≤0 时跳过同步
   */
  syncFromRig(state: CameraState, near: number, far: number, viewport: ViewportRect): void {
    // 视口尺寸为 0 时跳过同步，避免除零
    if (viewport.width <= 0 || viewport.height <= 0) return

    const previousMode = this._mode
    const modeChanged = previousMode !== state.mode
    this._mode = state.mode

    if (modeChanged) {
      const perspectiveState =
        previousMode === CameraMode.Orthographic && state.mode === CameraMode.Perspective
          ? this._createPerspectiveEquivalentState(state)
          : state
      const orthographicZoom =
        previousMode === CameraMode.Perspective && state.mode === CameraMode.Orthographic
          ? 1
          : state.zoom

      // 模式切换帧：同步两个相机，确保切换后状态一致，同时尽量保持视野尺度不突变
      this._syncPose(this.perspectiveCamera, perspectiveState)
      this._syncPose(this.orthographicCamera, state)
      this._syncPerspectiveProjection(perspectiveState, near, far, viewport)
      this._syncOrthographicProjection(state, near, far, viewport, orthographicZoom)
    } else if (this._mode === CameraMode.Perspective) {
      this._syncPose(this.perspectiveCamera, state)
      this._syncPerspectiveProjection(state, near, far, viewport)
    } else {
      this._syncPose(this.orthographicCamera, state)
      this._syncOrthographicProjection(state, near, far, viewport)
    }
  }

  /** 释放资源，将相机从场景图中移除 */
  dispose(): void {
    this.perspectiveCamera.removeFromParent()
    this.orthographicCamera.removeFromParent()
  }

  // ==================== 内部方法 ====================

  /** 同步相机姿态（位置、朝向、up 向量） */
  private _syncPose(camera: THREE.Camera, state: CameraState): void {
    camera.position.copy(state.position)
    camera.up.copy(state.up)
    camera.lookAt(state.target)
    if ('updateMatrixWorld' in camera && typeof camera.updateMatrixWorld === 'function') {
      camera.updateMatrixWorld(true)
    }
  }

  /** 同步透视相机投影参数 */
  private _syncPerspectiveProjection(
    state: CameraState,
    near: number,
    far: number,
    viewport: ViewportRect
  ): void {
    const cam = this.perspectiveCamera
    cam.fov = state.fov
    cam.aspect = viewport.width / viewport.height
    cam.near = near
    cam.far = far
    cam.updateProjectionMatrix()
  }

  /** 同步正交相机投影参数 */
  private _syncOrthographicProjection(
    state: CameraState,
    near: number,
    far: number,
    viewport: ViewportRect,
    zoomOverride?: number
  ): void {
    const cam = this.orthographicCamera
    const safeZoom = Math.max(zoomOverride ?? state.zoom, EPSILON)
    const halfHeight = this._getPerspectiveHalfHeight(state)
    const halfWidth = halfHeight * (viewport.width / viewport.height)

    cam.left = -halfWidth
    cam.right = halfWidth
    cam.top = halfHeight
    cam.bottom = -halfHeight
    cam.zoom = safeZoom
    cam.near = near
    cam.far = far
    cam.userData[ORTHOGRAPHIC_EQUIVALENT_FOV_USER_DATA_KEY] = state.fov
    cam.updateProjectionMatrix()
  }

  private _getPerspectiveHalfHeight(state: CameraState): number {
    const distance = Math.max(state.position.distanceTo(state.target), EPSILON)
    const fovRad = THREE.MathUtils.degToRad(state.fov)
    return Math.max(distance * Math.tan(fovRad / 2), EPSILON)
  }

  private _createPerspectiveEquivalentState(state: CameraState): CameraState {
    const safeZoom = Math.max(state.zoom, EPSILON)
    const offset = new THREE.Vector3().subVectors(state.position, state.target)

    if (offset.lengthSq() < EPSILON_ULTRA) {
      offset.set(0, -1, 0)
    } else {
      offset.normalize()
    }

    const distance = Math.max(state.position.distanceTo(state.target), EPSILON)
    const nextPosition = state.target.clone().addScaledVector(offset, distance / safeZoom)

    return {
      ...state,
      position: nextPosition,
    }
  }
}
